import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, TextField, InputAdornment, Skeleton, Chip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';

const T = {
  primary: '#526075', tertiary: '#635983', tertiaryContainer: '#dbcdff',
  primaryContainer: '#d5e3fd', bg: '#f8f9fa', surface: '#ffffff',
  surfaceLow: '#f1f4f5', text: '#2d3335', secondary: '#556071',
  outline: '#767c7e', outlineVariant: '#adb3b5', emerald: '#10b981',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

const SECTOR_COLORS = {
  'Key account': { color: '#f59e0b', bg: '#fef3c7' },
  'Premium account': { color: '#3b82f6', bg: '#dbeafe' },
  'Normaal account': { color: T.outline, bg: T.surfaceLow },
  'B2B': { color: '#3b82f6', bg: '#dbeafe' },
  'Semi-public': { color: T.tertiary, bg: T.tertiaryContainer },
  'Public': { color: T.emerald, bg: '#ecfdf5' },
  'Agency': { color: '#f59e0b', bg: '#fef3c7' },
  'Other': { color: T.outline, bg: T.surfaceLow },
};

function KlantCard({ klant, onClick }) {
  const naam = klant.fields?.Account || 'Onbekend';
  const sector = klant.fields?.Sector || '';
  const status = klant.fields?.Status || '';
  const beeldmerk = klant.fields?.Beeldmerk;
  const logoUrl = Array.isArray(beeldmerk) && beeldmerk.length > 0
    ? beeldmerk[0]?.thumbnails?.small?.url || beeldmerk[0]?.url
    : null;
  const sectorStyle = SECTOR_COLORS[sector] || SECTOR_COLORS.Other;
  const isInactive = status && status !== 'Actief';

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 2,
        bgcolor: T.surface, borderRadius: '0.625rem',
        px: 2, py: 1.5,
        cursor: 'pointer',
        border: '1px solid rgba(45,51,53,0.06)',
        transition: 'all 0.15s',
        opacity: isInactive ? 0.55 : 1,
        '&:hover': {
          borderColor: `${T.primary}25`,
          boxShadow: '0 4px 16px rgba(45,51,53,0.06)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      {/* Logo / Initialen */}
      {logoUrl ? (
        <Box component="img" src={logoUrl} alt=""
          sx={{
            width: 36, height: 36, borderRadius: '0.5rem', objectFit: 'contain',
            bgcolor: T.surfaceLow, p: 0.25, flexShrink: 0,
          }}
        />
      ) : (
        <Box sx={{
          width: 36, height: 36, borderRadius: '0.5rem',
          bgcolor: sectorStyle.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: sectorStyle.color, lineHeight: 1 }}>
            {naam.substring(0, 2).toUpperCase()}
          </Typography>
        </Box>
      )}

      {/* Naam */}
      <Typography noWrap sx={{
        fontFamily: T.fontBody, fontSize: '0.84rem', fontWeight: 600, color: T.text,
        flex: 1, minWidth: 0,
      }}>
        {naam}
      </Typography>

      {/* Sector badge */}
      {sector && (
        <Chip
          label={sector}
          size="small"
          sx={{
            fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 700, height: 20,
            color: sectorStyle.color, bgcolor: sectorStyle.bg,
            textTransform: 'uppercase', letterSpacing: '0.04em',
            flexShrink: 0,
          }}
        />
      )}

      {/* Inactive indicator */}
      {isInactive && (
        <Chip
          label={status}
          size="small"
          sx={{
            fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 700, height: 20,
            color: '#ef4444', bgcolor: '#fef2f2',
            flexShrink: 0,
          }}
        />
      )}

      {/* Chevron */}
      <Icon name="chevron_right" size={18} sx={{ color: T.outlineVariant, flexShrink: 0 }} />
    </Box>
  );
}

export default function KlantenList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [klanten, setKlanten] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await authFetch(`${BACKEND_URL}/Api/klanten`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        setKlanten(data.klanten || []);
      } catch (err) {
        setError(`Kon klanten niet laden: ${err.message}`);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filteredKlanten = useMemo(() => {
    if (!searchTerm) return klanten;
    const term = searchTerm.toLowerCase();
    return klanten.filter(k => {
      const naam = (k.fields?.Account || '').toLowerCase();
      const sector = (k.fields?.Sector || '').toLowerCase();
      return naam.includes(term) || sector.includes(term);
    });
  }, [klanten, searchTerm]);

  const sortedKlanten = useMemo(() => {
    return [...filteredKlanten].sort((a, b) => {
      const statusA = a.fields?.Status === 'Actief' ? 0 : 1;
      const statusB = b.fields?.Status === 'Actief' ? 0 : 1;
      if (statusA !== statusB) return statusA - statusB;
      return (a.fields?.Account || '').localeCompare(b.fields?.Account || '');
    });
  }, [filteredKlanten]);

  // Group by first letter
  const grouped = useMemo(() => {
    const map = {};
    sortedKlanten.forEach(k => {
      const letter = (k.fields?.Account || '?')[0].toUpperCase();
      if (!map[letter]) map[letter] = [];
      map[letter].push(k);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [sortedKlanten]);

  return (
    <Box sx={{ width: '100%', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 10 }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Box sx={{ width: 48, height: 48, borderRadius: '0.75rem', bgcolor: T.primaryContainer, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name="business" size={26} sx={{ color: T.primary }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontFamily: T.font, fontSize: '1.8rem', fontWeight: 800, color: T.text, letterSpacing: '-0.03em' }}>
            Klanten
          </Typography>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.secondary }}>
            {loading ? 'Laden...' : `${sortedKlanten.length} accounts`}
          </Typography>
        </Box>
      </Box>

      {/* Search */}
      <TextField
        size="small" fullWidth placeholder="Zoek op klant, sector..."
        value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: T.outlineVariant }} /></InputAdornment>,
        }}
        sx={{
          mb: 4, maxWidth: 480,
          '& .MuiOutlinedInput-root': {
            borderRadius: '9999px', fontSize: '0.84rem', bgcolor: T.surface,
            '& fieldset': { borderColor: 'rgba(45,51,53,0.08)' },
          },
        }}
      />

      {/* Error */}
      {error && (
        <Box sx={{ p: 2.5, mb: 4, borderRadius: '0.75rem', bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.84rem', color: '#991b1b' }}>{error}</Typography>
        </Box>
      )}

      {/* Loading skeleton */}
      {loading && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <Skeleton key={i} variant="rounded" height={52} sx={{ borderRadius: '0.625rem' }} />
          ))}
        </Box>
      )}

      {/* Empty state */}
      {!loading && !error && sortedKlanten.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Icon name="business" size={48} sx={{ color: T.outlineVariant, display: 'block', margin: '0 auto 16px' }} />
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.92rem', color: T.outline }}>
            {searchTerm ? 'Geen klanten gevonden' : 'Geen klanten beschikbaar'}
          </Typography>
        </Box>
      )}

      {/* List — grouped by letter, 2 columns */}
      {!loading && grouped.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {grouped.map(([letter, items]) => (
            <Box key={letter}>
              <Typography sx={{
                fontFamily: T.font, fontSize: '0.72rem', fontWeight: 800, color: T.outlineVariant,
                textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1, ml: 0.5,
              }}>
                {letter}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
                {items.map(klant => (
                  <KlantCard key={klant.id} klant={klant} onClick={() => navigate(`/accounts/${klant.id}/workspace`)} />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
